import React, { useState, useEffect } from 'react';
import { Upload, Download, GitMerge, FileText, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import api from '../services/api';

const MergeDatasets = () => {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset1, setSelectedDataset1] = useState(null);
  const [selectedDataset2, setSelectedDataset2] = useState(null);
  const [dataset1Columns, setDataset1Columns] = useState([]);
  const [dataset2Columns, setDataset2Columns] = useState([]);
  const [mergeColumn1, setMergeColumn1] = useState('');
  const [mergeColumn2, setMergeColumn2] = useState('');
  const [mergeType, setMergeType] = useState('inner');
  const [mergeResult, setMergeResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Select datasets, 2: Configure merge, 3: View results

  // Fetch available datasets on component mount
  useEffect(() => {
    fetchDatasets();
  }, []);

  const fetchDatasets = async () => {
    try {
      const response = await api.get('/datasets');
      // Filter only CSV datasets (not image datasets)
      const csvDatasets = response.data.filter(dataset => 
        dataset.metadata?.type !== 'image_classification'
      );
      setDatasets(csvDatasets);
    } catch (error) {
      setError('Failed to fetch datasets');
      console.error('Error fetching datasets:', error);
    }
  };

  // Fetch columns for a specific dataset
  const fetchDatasetColumns = async (datasetId, setColumns) => {
    try {
      const response = await api.get(`/datasets/${datasetId}`);
      const lines = response.data.split('\n');
      if (lines.length > 0) {
        const columns = lines[0].split(',').map(col => col.trim());
        setColumns(columns);
      }
    } catch (error) {
      console.error('Error fetching dataset columns:', error);
      setError('Failed to fetch dataset columns');
    }
  };

  const handleDataset1Change = (dataset) => {
    setSelectedDataset1(dataset);
    if (dataset) {
      fetchDatasetColumns(dataset._id, setDataset1Columns);
    } else {
      setDataset1Columns([]);
    }
    setMergeColumn1('');
  };

  const handleDataset2Change = (dataset) => {
    setSelectedDataset2(dataset);
    if (dataset) {
      fetchDatasetColumns(dataset._id, setDataset2Columns);
    } else {
      setDataset2Columns([]);
    }
    setMergeColumn2('');
  };

  const handleMergeDatasets = async () => {
    if (!selectedDataset1 || !selectedDataset2 || !mergeColumn1 || !mergeColumn2) {
      setError('Please select both datasets and merge columns');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('/datasets/merge', {
        dataset1_id: selectedDataset1._id,
        dataset2_id: selectedDataset2._id,
        merge_column1: mergeColumn1,
        merge_column2: mergeColumn2,
        merge_type: mergeType
      });

      setMergeResult(response.data);
      setStep(3);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to merge datasets');
      console.error('Error merging datasets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadMerged = async () => {
    if (!mergeResult?.merge_id) return;

    try {
      const response = await api.get(`/datasets/merge/${mergeResult.merge_id}/download`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `merged_dataset_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError('Failed to download merged dataset');
      console.error('Error downloading merged dataset:', error);
    }
  };

  const resetMerge = () => {
    setStep(1);
    setSelectedDataset1(null);
    setSelectedDataset2(null);
    setDataset1Columns([]);
    setDataset2Columns([]);
    setMergeColumn1('');
    setMergeColumn2('');
    setMergeResult(null);
    setError(null);
  };

  const DatasetSelector = ({ title, selectedDataset, onDatasetChange, columns, mergeColumn, onMergeColumnChange }) => {
    const [uploadMode, setUploadMode] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = React.useRef(null);

    const handleFileUpload = async (file) => {
      if (!file) return;
      
      setIsUploading(true);
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/datasets', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Update datasets list and select the new dataset
        setDatasets(prev => [...prev, response.data]);
        onDatasetChange(response.data);
        setUploadMode(false);
        
      } catch (error) {
        setError('Error uploading file: ' + (error.response?.data?.message || error.message));
      } finally {
        setIsUploading(false);
      }
    };

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-blue-600" />
          {title}
        </h3>
        
        <div className="space-y-4">
          {/* Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setUploadMode(false)}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                !uploadMode 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Select Existing
            </button>
            <button
              onClick={() => setUploadMode(true)}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                uploadMode 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Upload New
            </button>
          </div>

          {!uploadMode ? (
            /* Existing Dataset Selection */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Dataset
              </label>
              <select
                value={selectedDataset?._id || ''}
                onChange={(e) => {
                  const dataset = datasets.find(d => d._id === e.target.value);
                  onDatasetChange(dataset);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choose a dataset...</option>
                {datasets.map(dataset => (
                  <option key={dataset._id} value={dataset._id}>
                    {dataset.name} ({dataset.metadata?.rowCount || 0} rows)
                  </option>
                ))}
              </select>
            </div>
          ) : (
            /* File Upload */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload CSV File
              </label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors cursor-pointer"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  className="hidden"
                  accept=".csv,.xlsx,.xls"
                />
                <div className="flex flex-col items-center justify-center">
                  {isUploading ? (
                    <>
                      <Loader className="w-8 h-8 text-blue-500 mb-2 animate-spin" />
                      <span className="text-sm text-gray-600">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Click to upload CSV file</span>
                      <span className="text-xs text-gray-500 mt-1">Max size: 10MB</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {columns.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Merge Column
              </label>
              <select
                value={mergeColumn}
                onChange={(e) => onMergeColumnChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Choose a column...</option>
                {columns.map(column => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedDataset && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                <strong>Dataset:</strong> {selectedDataset.name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Rows:</strong> {selectedDataset.metadata?.rowCount || 0}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Columns:</strong> {columns.join(', ')}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <GitMerge className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Merge Datasets</h1>
          </div>
          <p className="text-gray-600">
            Combine two CSV datasets based on a common column
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= stepNum 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {stepNum}
                </div>
                {stepNum < 3 && (
                  <div className={`w-16 h-1 ml-4 ${
                    step > stepNum ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-2">
            <div className="flex space-x-20 text-sm text-gray-600">
              <span>Select Datasets</span>
              <span>Configure Merge</span>
              <span>View Results</span>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Step 1 & 2: Dataset Selection and Configuration */}
        {step <= 2 && (
          <div className="space-y-6">
            {/* Dataset Selection */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DatasetSelector
                title="First Dataset"
                selectedDataset={selectedDataset1}
                onDatasetChange={handleDataset1Change}
                columns={dataset1Columns}
                mergeColumn={mergeColumn1}
                onMergeColumnChange={setMergeColumn1}
              />
              
              <DatasetSelector
                title="Second Dataset"
                selectedDataset={selectedDataset2}
                onDatasetChange={handleDataset2Change}
                columns={dataset2Columns}
                mergeColumn={mergeColumn2}
                onMergeColumnChange={setMergeColumn2}
              />
            </div>

            {/* Merge Configuration */}
            {selectedDataset1 && selectedDataset2 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Merge Configuration
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Merge Type
                    </label>
                    <select
                      value={mergeType}
                      onChange={(e) => setMergeType(e.target.value)}
                      className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="inner">Inner Join</option>
                      <option value="left">Left Join</option>
                      <option value="right">Right Join</option>
                      <option value="outer">Outer Join</option>
                    </select>
                    <p className="text-sm text-gray-500 mt-1">
                      Choose how to combine the datasets when merge columns don't match
                    </p>
                  </div>

                  <button
                    onClick={handleMergeDatasets}
                    disabled={!mergeColumn1 || !mergeColumn2 || isLoading}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Merging...
                      </>
                    ) : (
                      <>
                        <GitMerge className="w-5 h-5 mr-2" />
                        Merge Datasets
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && mergeResult && (
          <div className="space-y-6">
            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-green-800">
                Successfully merged datasets! {mergeResult.rows_count} rows in the result.
              </span>
            </div>

            {/* Results Preview */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Preview (First 10 rows)
                </h3>
                <div className="flex space-x-3">
                  <button
                    onClick={handleDownloadMerged}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV
                  </button>
                  <button
                    onClick={resetMerge}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-700"
                  >
                    New Merge
                  </button>
                </div>
              </div>

              {/* Data Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {mergeResult.columns.map((column, index) => (
                        <th
                          key={index}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mergeResult.preview_data.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {mergeResult.columns.map((column, colIndex) => (
                          <td
                            key={colIndex}
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                          >
                            {row[column] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MergeDatasets;